import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import en from '../locales/en.json';
import hi from '../locales/hi.json';

const locales = { en, hi };

/**
 * Deep key lookup: supports both flat 'nav.home' and nested { nav: { home: '...' } }
 */
function deepGet(obj, key) {
  const parts = key.split('.');
  let result = obj;
  for (const part of parts) {
    if (result == null || typeof result !== 'object') return null;
    result = result[part];
  }
  return typeof result === 'string' ? result : null;
}

/** Detect browser language — default to 'en', switch to 'hi' only if explicitly Hindi */
function detectBrowserLanguage() {
  const saved = localStorage.getItem('yit_language');
  if (saved === 'en' || saved === 'hi') return saved;
  const browserLang = navigator.language || navigator.userLanguage || 'en';
  return browserLang.startsWith('hi') ? 'hi' : 'en';
}

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(detectBrowserLanguage);

  // Persist to localStorage whenever language changes
  useEffect(() => {
    localStorage.setItem('yit_language', language);
    // Update <html lang> attribute for SEO/accessibility
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (lang === 'en' || lang === 'hi') setLanguageState(lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState(prev => prev === 'en' ? 'hi' : 'en');
  }, []);

  /**
   * t(key) — translate a key like 'nav.home' or 'hero.title'
   * Falls back to English, then to the key itself
   */
  const t = useCallback((key) => {
    return deepGet(locales[language], key)
      || deepGet(locales['en'], key)
      || key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
}

export default LanguageContext;

